import type { Bundle, Practitioner, Reference } from '@medplum/fhirtypes'
import { formatDateTime } from '@seen/core/dates'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import {
  approvedEntryHandles,
  getEntryDecisions,
  getFinalCommittedAt,
  getFinalCommitter,
  rejectedEntryHandles,
} from '@seen/core/queued-edits/decisions'
import type {
  ResourceChangeSet,
  ResourceDecision,
} from '@seen/core/queued-edits/model'
import { getReferenceString } from '@seen/core/references'
import { cn } from '@seen/ui'
import { IconChecks, IconChevronDown, IconSparkles } from '@tabler/icons-react'
import { type JSX, useMemo, useState } from 'react'
import { AvatarCircles } from '#src/components/core/AvatarCircles.tsx'
import { HoverPractitionerAvatar } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { QueuedEditsProposalSource } from '#src/components/queued-edits/QueuedEditsProposalSource.tsx'
import { ResourceDiffCard } from '#src/components/queued-edits/ResourceDiffCard.tsx'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

const MAX_VISIBLE_AVATARS = 5

export interface QueuedEditsReviewSummaryProps {
  // The applied proposal Bundle — the self-contained review record. Carries the
  // per-entry decisions and the final committer the summary reads.
  bundle: Bundle
  // The computed change sets (same list the review surface used), so the
  // expanded view can render the per-resource diffs read-only. Empty is fine —
  // the summary still renders counts from the bundle's recorded decisions.
  changes: ResourceChangeSet[]
  // AI summary of the proposal, if the bundle carried one.
  aiSummary?: string
}

// Collapsed post-review panel for an applied proposal Bundle. Shows the AI
// summary, an overview of the outcome (counts), who was involved (the distinct
// per-entry deciders), and who executed the final application. Expands to the
// full per-resource diff list — strictly read-only; decisions can no longer be
// changed once applied.
export function QueuedEditsReviewSummary({
  bundle,
  changes,
  aiSummary,
}: QueuedEditsReviewSummaryProps): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  const decisions = useMemo(() => getEntryDecisions(bundle), [bundle])
  const approvedCount = approvedEntryHandles(bundle).length
  const rejectedCount = rejectedEntryHandles(bundle).length

  // Distinct practitioners who decided any entry — "who was involved".
  const involved = useMemo(() => {
    const seen = new Map<string, Reference<Practitioner>>()
    for (const decision of decisions) {
      if (!decision.by) {
        continue
      }
      const key = getReferenceString(decision.by)
      if (!seen.has(key)) {
        seen.set(key, decision.by)
      }
    }
    return [...seen.values()]
  }, [decisions])

  const finalCommitter = getFinalCommitter(bundle)
  const finalCommittedAt = getFinalCommittedAt(bundle)

  // Map each change to its recorded decision/decider for the read-only cards.
  const decisionByHandle = useMemo(
    () => new Map(decisions.map((d) => [d.entry, d])),
    [decisions]
  )
  const handleOf = (change: ResourceChangeSet): string =>
    change.reference ?? change.fullUrl ?? change.id

  // The orchestrating review task, if this bundle was filed as one. Surfaced at
  // the bottom of the expanded panel so the completed review links back to its
  // inbox task.
  const reviewTask = useSearchOne(
    QueuedEditsReviewTask,
    bundle.id
      ? QueuedEditsReviewTask.byBundle({ reference: `Bundle/${bundle.id}` })
      : undefined
  )

  return (
    <div className="flex w-full min-w-0 flex-col rounded-lg border border-border bg-card">
      {/* Collapsed header — click to expand the read-only diffs. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-muted/40"
        aria-expanded={expanded}
      >
        <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-pine-100">
          <IconChecks className="size-4 text-pine-600" />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium">
              Review complete —{' '}
              {approvedCount > 0
                ? `${approvedCount} ${approvedCount === 1 ? 'change' : 'changes'} applied`
                : 'all changes rejected'}
              {rejectedCount > 0 &&
                approvedCount > 0 &&
                `, ${rejectedCount} rejected`}
            </p>
            <IconChevronDown
              className={cn(
                'ml-auto size-4 shrink-0 text-muted-foreground transition-transform',
                expanded && 'rotate-180'
              )}
            />
          </div>

          {aiSummary && (
            <p className="max-w-none text-xs text-muted-foreground">
              <IconSparkles className="mr-1 inline size-3 shrink-0 text-primary" />
              {aiSummary}
            </p>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-2">
            {involved.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-2xs tracking-wide text-muted-foreground/70 uppercase">
                  Involved
                </span>
                <AvatarCircles
                  avatars={involved.slice(0, MAX_VISIBLE_AVATARS)}
                  numInLastCircle={
                    involved.length > MAX_VISIBLE_AVATARS
                      ? involved.length - MAX_VISIBLE_AVATARS
                      : undefined
                  }
                />
              </div>
            )}

            {finalCommitter && (
              <div className="flex items-center gap-2">
                <span className="text-2xs tracking-wide text-muted-foreground/70 uppercase">
                  Applied by
                </span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <HoverPractitionerAvatar
                    practitioner={finalCommitter}
                    size="icon"
                  />
                  {finalCommittedAt && (
                    <span className="text-muted-foreground/60">
                      {formatDateTime(finalCommittedAt)}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </button>

      {/* Expanded — read-only per-resource diffs, scrollable so a large review
          doesn't blow out the panel. No approve/reject handlers, so
          ResourceDiffCard renders without decision controls. */}
      {expanded && changes.length > 0 && (
        <div className="border-t border-border">
          {/* Caps at a max height and scrolls past it; shrinks to content below
              the cap. `!max-h` + `h-auto!` (important) override the ScrollArea
              root's own `size-full`; the contentClassName caps the Radix
              viewport too so its `size-full` doesn't outgrow the cap and defeat
              the scroll. */}
          <ScrollArea
            scrollRestorationId={`queued-edits-summary-${bundle.id}`}
            className="h-auto! max-h-[480px]!"
            contentClassName="max-h-[480px]!"
          >
            {changes.map((change) => {
              const recorded = decisionByHandle.get(handleOf(change))
              return (
                <ResourceDiffCard
                  key={change.id}
                  change={change}
                  decision={recorded?.decision as ResourceDecision | undefined}
                  decidedBy={recorded?.by}
                  decidedAt={recorded?.at}
                />
              )
            })}
          </ScrollArea>
          {/* Applied-disclaimer + provenance on the left, the originating task
              on the right, all on one full-width footer row. The border lives
              on this row (not the scroll content) so it spans the whole card
              even on wide surfaces. */}
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-t border-border px-5 py-2 text-2xs text-muted-foreground/60">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                This review has been applied and can no longer be changed.
              </span>
              {bundle.id && (
                <QueuedEditsProposalSource
                  bundleReference={`${bundle.resourceType}/${bundle.id}`}
                  className="min-w-0 truncate text-2xs"
                />
              )}
            </div>
            {reviewTask.data && (
              <span className="text-xs">
                <HoverTask task={reviewTask.data.reference} />
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
